import { useCallback, useState, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useRenderStore } from '../model/render-form.store';
import { toast } from '@/shared/lib/toast';
import { createLogger } from '@/shared/lib/logger';
import type { RenderCreateRequest } from '@/types/render';
import { ClassTypes } from '@/shared/constants/class-types';
import { goalsAutosaveCoordinator } from '@/features/project/lib/goals-autosave-coordinator';

const logger = createLogger('RenderSubmit');

interface UseRenderSubmitOptions {
  isDerivative?: boolean;
  onSubmit: (data: RenderCreateRequest) => Promise<void>;
  conceptAngleId?: string;
  /// Project to flush goals for before posting the render. The server
  /// snapshots `project.goals` into the new render at create time, so a
  /// pending PATCH must land first or the render captures stale goals.
  projectId?: string;
}

// Dedicated hook for render submission
export function useRenderSubmit({
  isDerivative = false,
  onSubmit,
  conceptAngleId,
  projectId,
}: UseRenderSubmitOptions) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const generateNextSeed = useRenderStore((state) => state.generateNextSeed);
  const resetChangesToDefaults = useRenderStore(
    (state) => state.resetChangesToDefaults
  );
  const tCommon = useTranslations('common');

  // Plan/balance/queue gates live in `useRenderPreflight` and run from the
  // Generate-button click handler (Apple parity with RenderSettingsSheet:
  // preflight in the toolbar handler, server 422s caught downstream). Don't
  // re-gate here — a silent return at this layer is what made the drawer
  // button look dead instead of routing to the paywall.

  const handleSubmit = useCallback(
    async () => {
      if (isSubmitting) return;

      try {
        setIsSubmitting(true);

        // Flush any in-flight Project goals edit before POSTing /renders.
        // Server snapshots project.goals into the new render at create time,
        // so a pending PATCH must land first. No-op when nothing is pending.
        if (projectId) {
          const saved = await goalsAutosaveCoordinator.flushAndAwait(projectId);
          if (!saved) {
            toast.error(tCommon('error'), {
              description: tCommon('errors.generic'),
            });
            setIsSubmitting(false);
            return;
          }
        }

        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Set timeout to re-enable button after 10 seconds (only for derivatives)
        if (isDerivative) {
          timeoutRef.current = setTimeout(() => {
            setIsSubmitting(false);
            toast.warning(tCommon('warning'), {
              description: tCommon('errors.generic'),
            });
          }, 10000);
        }

        // Generate next seed based on mode (Refine keeps it; Explore re-rolls).
        generateNextSeed();

        // Get updated formData after seed generation
        const currentFormData = useRenderStore.getState().formData;

        logger.info('Current formData:', {
          isDerivative,
          formData: currentFormData,
          output_style_id: currentFormData.output_style_id,
          prompts: currentFormData.prompts,
          environment_setting: currentFormData.environment_setting,
        });

        // Prepare data for request - remove UI-only fields
        const { ...requestData } = currentFormData;

        // Ensure null values stay null (don't get converted to defaults):
        // [] and null both mean "no selection / auto" — normalize to null.
        if (
          requestData.style_ids === null ||
          (Array.isArray(requestData.style_ids) &&
            requestData.style_ids.length === 0)
        ) {
          requestData.style_ids = null;
        }
        if (
          requestData.material_ids === null ||
          (Array.isArray(requestData.material_ids) &&
            requestData.material_ids.length === 0)
        ) {
          requestData.material_ids = null;
        }
        if (
          requestData.colors === null ||
          (Array.isArray(requestData.colors) &&
            requestData.colors.length === 0)
        ) {
          requestData.colors = null;
        }

        // ONE payload builder for main AND derivative renders (Apple/Android
        // parity — both use a single builder). project_goals and
        // construction_region are intentionally omitted: the server snapshots
        // them from Project at render-create time. Derivatives MUST send the
        // reference fields and auto-flags too — the server defaults missing
        // auto-flags to TRUE, so omitting them silently auto-injected project
        // references into derivative renders that Apple/Android suppress, and
        // dropped Selective/output reference picks.
        const renderData: RenderCreateRequest = {
          data: {
            type: ClassTypes.RENDER,
            attributes: {
              version: requestData.version,
              seed: requestData.seed,
              style_ids: requestData.style_ids,
              colors: requestData.colors,
              material_ids: requestData.material_ids,
              prompts: requestData.prompts,
              controls: requestData.controls,
              output_style_id: requestData.output_style_id,
              environment_setting: requestData.environment_setting,
              render_model_family: requestData.render_model_family,
              render_effort: requestData.render_effort,
              // Project references by category
              style_project_reference_ids:
                requestData.style_project_reference_ids,
              material_project_reference_ids:
                requestData.material_project_reference_ids,
              color_project_reference_ids:
                requestData.color_project_reference_ids,
              output_project_reference_ids:
                requestData.output_project_reference_ids,
              // Auto-use project references flags
              use_auto_style_refs: requestData.use_auto_style_refs,
              use_auto_material_refs: requestData.use_auto_material_refs,
              use_auto_color_refs: requestData.use_auto_color_refs,
              // Refine: when 'refine', server uses parent_render_id's image as
              // the pipeline input and switches to the refinement template.
              mode: requestData.mode,
              parent_render_id:
                requestData.mode === 'refine'
                  ? requestData.parent_render_id
                  : null,
              // Hi-Res toggle: ~2K native (Pro+ plan). Server rejects with
              // HIGH_RESOLUTION_RENDER_FEATURE_UNAVAILABLE if plan lacks it.
              is_hires: requestData.is_hires,
              // Extended thinking toggle: stronger reasoning model for the
              // design prompt (Pro+ plan). Server rejects with
              // EXTENDED_THINKING_FEATURE_UNAVAILABLE if plan lacks it.
              is_extended_thinking: requestData.is_extended_thinking,
            },
          },
        };

        logger.info('Final renderData to send:', {
          isDerivative,
          renderData,
          output_style_id: renderData.data.attributes.output_style_id,
          prompts: renderData.data.attributes.prompts,
          environment_setting: renderData.data.attributes.environment_setting,
        });

        await onSubmit(renderData);

        // Note: resetChangesToDefaults is now called by RenderSettingsDrawer
        // after it updates defaultValues with the submitted settings.
        // This ensures formData is reset to the NEW settings, not the old ones.
      } finally {
        // Clear timeout when request completes
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      generateNextSeed,
      resetChangesToDefaults,
      isDerivative,
      conceptAngleId,
      projectId,
      onSubmit,
      tCommon,
    ]
  );

  // Cleanup timeout on unmount
  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  return {
    submitRender: handleSubmit,
    isSubmitting,
    cleanup,
  };
}
